<template>
  <div class="resource-listing">
    <div class="wrapper">
      <div class="breadcrumb">
        <template v-if="showHiddenBreadcrumbs">
          <cv-link
            v-for="breadcrumb in preHiddenBreadcrumbs"
            :key="breadcrumb.folderName"
            @click="handleOpen(breadcrumb)"
          >
            {{ breadcrumb.folderName }}
          </cv-link>
        </template>
        <overflow-menu v-if="showHiddenBreadcrumbs">
          <template #trigger>
            <svg-icon name="Kebab24" kind="action" />
          </template>
          <OverflowMenuItem
            v-for="hiddenBreadcrumb in hiddenBreadcrumbs"
            :key="hiddenBreadcrumb.folderName"
            @click="handleOpen(hiddenBreadcrumb)"
          >
            {{ hiddenBreadcrumb.folderName }}
          </OverflowMenuItem>
        </overflow-menu>
        <cv-link
          v-for="breadcrumb in breadcrumbs"
          :key="breadcrumb.folderName"
          @click="handleOpen(breadcrumb)"
        >
          <span>{{ breadcrumb.folderName }}</span>
        </cv-link>
      </div>
      <cv-button
        kind="primary"
        size="field"
        :disabled="isRootFolder"
        @click="handleCreateNewFolder"
      >
        <svg-icon name="Plus24" kind="action" />
        {{ t("new_folder") }}
      </cv-button>
    </div>
    <data-table-list>
      <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
        mobile-breakpoint="0"
      -->
      <v-data-table-server
        v-model="selectedInner"
        v-model:sort-by="options.sortBy"
        :class="classes"
        :headers="headersData"
        :items="filesData"
        :items-length="totalRecords"
        show-select
        item-selectable="selectable"
        :select-strategy="selectStrategy"
        fixed-header
        item-value="uuid"
        :items-per-page="options.itemsPerPage"
        mobile-breakpoint="0"
        @update:options="handleSort"
      >
        <template #[`item.title`]="{ item }">
          <data-table-cell
            class="info"
            :class="{ active: sortFields === 'name.raw' && sortingActive }"
          >
            <div class="title">
              <span class="bx--text-truncate--end">
                <svg-icon name="Folder32" kind="indicative" />
                <span class="text" :title="item.title">{{ item.title }}</span>
              </span>
            </div>
          </data-table-cell>
        </template>

        <template #[`item.action`]="{ item }">
          <data-table-cell class="action">
            <cv-link @click="handleOpenFolder(item.uuid)">
              <svg-icon name="CircleArrowRight16" kind="action" />
              {{ t("open_folder") }}</cv-link
            >
          </data-table-cell>
        </template>
        <template #bottom></template>
      </v-data-table-server>
    </data-table-list>
  </div>
</template>

<script lang="ts" setup>
import { loadLocaleMessages } from "@plugins/i18n";
import { RESOURCE_NODE_TYPE } from "@modules/my-drive/my-drive-helper.ts";
import { RESOURCE_TYPE } from "@plugins/common/constant/resource.constant.ts";
import MyDriveApi from "@apis/my.drive.api";
import DateTimeFormatter from "@utils/datetime.formatter";
import MyDriveResourceNodeEditModal from "@modules/my-drive/components/MyDriveResourceNodeEditModal.vue";
import myDriveEventBus from "@modules/my-drive/service/event-bus";
import DatatableUtils from "@plugins/vuetify/service/datatable.utils";
import RandomUtils from "@utils/random.utils";
import { ref, computed, watch, onMounted } from "vue";
import { useModal } from "@/plugins/modal/composables/modal-instance";
import { useToast } from "@/plugins/toast/composables/toast-instance";
import { useI18n } from "vue-i18n";
import { useVm } from "@utils/vm.ts";
import { useErrorHandler } from "@plugins/common/composables/error";
import { useDirty } from "@plugins/common/composables/page-dirty";

const emit = defineEmits(["onResourceSelected"]);

const { vm } = useVm();
const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const toastHandler = useToast();
const modalHandler = useModal();

// Data
const headersData = ref([]);
const filesData = ref([]);
const totalRecords = ref(0);
const start = ref(0);
const itemsPerPage = ref(1000);
const breadcrumbs = ref([]);
const preHiddenBreadcrumbs = ref([]);
const hiddenBreadcrumbs = ref([]);
const showHiddenBreadcrumbs = ref(false);
const loading = ref(true);
const selected = ref([]);
const selectedInner = ref([]);
const options = ref({});
const isRootFolder = ref(true);
const sortDirections = ref("");
const sortFields = ref("");
const currentFolderUuid = ref("");
const rootFolderUuid = ref(RandomUtils.randomUuid());

// Props
interface Props {
  type?: string;
  excludedUuids?: string[]; // assuming UUIDs are strings
  singleSelection?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  singleSelection: true,
});

// Computed
const dirtyId = computed(() => {
  return "ResourceListing";
});

const { removeDirty, setDirty } = useDirty(dirtyId);

const classes = computed(() => {
  return {
    "resource-table": true,
    "_items-selectable": true,
    "_single-select": props.singleSelection,
  };
});

const isExcludedUuid = (file: { uuid: string }) => {
  return props.excludedUuids?.includes(file.uuid);
};

const sortingActive = computed(() => {
  return options.value.sortBy.length > 0;
});

const selectStrategy = computed(() => {
  return props.singleSelection ? "single" : "page";
});

// Methods
const initDefaultData = function () {
  filesData.value.push({
    title: t("my_drive_lessons_tab_label"),
    uuid: rootFolderUuid.value,
    isRoot: true,
  });
  totalRecords.value = 1;
  breadcrumbs.value.push({
    folderName: t("my_drive"),
    folderUuid: "",
  });
  options.value = {
    page: 1,
    itemsPerPage: itemsPerPage.value,
    sortBy: [{ key: "title", order: "desc" }],
  };
  selectedInner.value = filesData.value.map((file) => {
    return file.uuid;
  });
};

const fetchMyDriveData = function (uuid) {
  if (uuid === rootFolderUuid.value) {
    currentFolderUuid.value = undefined;
  } else {
    currentFolderUuid.value = uuid;
  }
  MyDriveApi.fetchMyDrive({
    //OK
    keywords: "",
    cacheable: false,
    draw: 1,
    start: start.value,
    length: itemsPerPage.value,
    parentNodeUuid: currentFolderUuid.value,
    currentPage: "LIBRARY",
    resourceNodeType: props.type,
    sortFields: sortFields.value,
    sortDirections: sortDirections.value,
    timeStamp: Date.now(),
  }).then((response) => {
    if (response.code === "ok") {
      filesData.value.splice(0, filesData.value.length);
      breadcrumbs.value.splice(0, breadcrumbs.value.length);
      preHiddenBreadcrumbs.value.splice(0, preHiddenBreadcrumbs.value.length);
      hiddenBreadcrumbs.value.splice(0, hiddenBreadcrumbs.value.length);
      showHiddenBreadcrumbs.value = false;

      if (!isRootFolder.value) {
        populateFilesData(response.data.data);
        totalRecords.value = response.data.recordsTotal;
      } else {
        filesData.value.push({
          title: t("my_drive_lessons_tab_label"),
          uuid: rootFolderUuid.value,
          isRoot: true,
        });
        totalRecords.value = 1;
      }
      createBreadcrumb(response.data.currentNode);
      removeDirty(dirtyId.value);
      selectedInner.value = [];
    } else {
      passiveApiFailHandler();
    }
  }, passiveApiFailHandler);
};

const populateFilesData = function (data) {
  data.forEach((file) => {
    if (!isExcludedUuid(file)) {
      const fileData = {};
      fileData.title = file.name;
      fileData.permission = file.permission;
      fileData.fileUuid = file.uuid;
      fileData.parentNodeId = file.parentNodeId;

      if (file.resourceNodeType === RESOURCE_NODE_TYPE.FOLDER) {
        fileData.uuid = file.uuid;
        fileData.type = file.resourceNodeType;
        fileData.modifiedBy = file.nodeModifiedByName;
        fileData.modifiedDate = DateTimeFormatter.formatLocalDateTimeToDateOnly(
          file.nodeModifiedDate,
        );
      } else {
        fileData.type = file.resourceNodeFileType;
        if (fileData.type === RESOURCE_TYPE.LESSON) {
          //lesson types: COURSE/NEW/URL/HTML
          fileData.lessonType = file.resourceNodeFileSubType;
        }
        fileData.subtype =
          fileData.type === RESOURCE_TYPE.LESSON
            ? file.resourceNodeFileMetaData.type // lesson learning type
            : file.resourceNodeFileSubType;
        fileData.uuid = file.resourceNodeFileMetaData.uuid;
        fileData.modifiedBy = file.resourceNodeFileMetaData.fileModifiedByName;
        fileData.modifiedDate = DateTimeFormatter.formatLocalDateTimeToDateOnly(
          file.resourceNodeFileMetaData.fileModifiedDate,
        );
        fileData.shared = file.shared;
      }
      filesData.value.push(fileData);
    }
  });
};

const createBreadcrumb = function (currentNode) {
  if (currentNode !== null) {
    if (currentNode.breadcrumbs.length <= 3) {
      breadcrumbs.value.push({ folderName: t("my_drive"), folderUuid: "" });
      if (!isRootFolder.value) {
        breadcrumbs.value.push({
          folderName: t("my_drive_lessons_tab_label"),
          folderUuid: "",
        }); //TODO (Calvin): Check if this condition is still needed
      }
      currentNode.breadcrumbs.forEach((breadcrumb) => {
        const breadcrumbData = {};
        breadcrumbData.folderName = breadcrumb.name;
        breadcrumbData.folderUuid = breadcrumb.uuid;
        breadcrumbs.value.push(breadcrumbData);
      });
    } else {
      showHiddenBreadcrumbs.value = true;
      preHiddenBreadcrumbs.value.push({ folderName: t("my_drive"), folderUuid: "" });
      hiddenBreadcrumbs.value.push({
        folderName: t("my_drive_lessons_tab_label"),
        folderUuid: "",
      });
      currentNode.breadcrumbs.forEach(function (breadcrumb, index) {
        const breadcrumbData = {};
        breadcrumbData.folderName = breadcrumb.name;
        breadcrumbData.folderUuid = breadcrumb.uuid;
        if (
          index === currentNode.breadcrumbs.length - 1 ||
          index === currentNode.breadcrumbs.length - 2
        ) {
          breadcrumbs.value.push(breadcrumbData);
        } else {
          hiddenBreadcrumbs.value.push(breadcrumbData);
        }
      }, vm);
    }
  } else {
    breadcrumbs.value.push({ folderName: t("my_drive"), folderUuid: "" });
    if (!isRootFolder.value) {
      breadcrumbs.value.push({
        folderName: t("my_drive_lessons_tab_label"),
        folderUuid: "",
      });
    }
  }
};

const handleOpen = function (data) {
  if (data.folderName === t("my_drive")) {
    isRootFolder.value = true;
  } else {
    isRootFolder.value = false;
  }
  fetchMyDriveData(data.folderUuid);
};

const handleOpenFolder = function (uuid) {
  isRootFolder.value = false;
  fetchMyDriveData(uuid);
};

const handleSort = function (options) {
  if (currentFolderUuid.value === "") {
    return;
  }
  if (options.sortBy.length > 0) {
    const sortColumn = options.sortBy[0].key;
    const sortDirection = options.sortBy[0].order === "desc" ? "descending" : "ascending";

    if (sortColumn === "type") {
      sortFields.value = [
        "fileTypeSortingOrder",
        "resourceNodeFileSubType",
        "resourceNodeFileMetaData.type",
        "name.raw",
      ].toString();
      sortDirections.value = [sortDirection, sortDirection, sortDirection, "ascending"].toString();
    } else if (sortColumn === "title") {
      sortFields.value = "name.raw";
      sortDirections.value = sortDirection;
    } else if (sortColumn === "modifiedDate") {
      sortFields.value = ["nodeModifiedDate", "name.raw"].toString();
      sortDirections.value = [sortDirection, "ascending"].toString();
    }
    fetchMyDriveData(currentFolderUuid.value);
  }
};

const handleFolderCreated = function () {
  const uuid =
    breadcrumbs.value.length > 0 ? breadcrumbs.value[breadcrumbs.value.length - 1].folderUuid : "";
  fetchMyDriveData(uuid);
  toastHandler.show("success", {
    title: t("create_folder_success_toast"),
  });
};

const handleCreateNewFolder = function () {
  const eventBusWatchInstances = [];
  eventBusWatchInstances.push(myDriveEventBus.$on("resourceNodeUpdated", handleFolderCreated));
  modalHandler.show(MyDriveResourceNodeEditModal, {
    resourceType: RESOURCE_TYPE.FOLDER,
    isRenamingResourceNode: false,
    resourceNodeTitle: t("default_folder_name"),
    currentPage: "LIBRARY",
    folderUuid:
      breadcrumbs.value.length > 0
        ? breadcrumbs.value[breadcrumbs.value.length - 1].folderUuid
        : "",
    eventBusWatchInstances: eventBusWatchInstances,
  });
};

const handleDirtyData = function () {
  if (selectedInner.value.length === 0) {
    removeDirty(dirtyId.value);
  } else {
    setDirty(dirtyId.value);
  }
};

// Watch
watch(
  selectedInner,
  () => {
    DatatableUtils.filterSelected(selectedInner.value, filesData.value, "uuid", selected.value);
    emit("onResourceSelected", selected.value);
    if (!loading.value) {
      handleDirtyData();
    }
  },
  { deep: true },
);

onMounted(async () => {
  // Created
  await Promise.all([loadLocaleMessages("my-drive-aria-label"), loadLocaleMessages("my-drive")]);
  headersData.value = [
    {
      title: t("folder_name"),
      class: "folder-name",
      key: "title",
      align: "start",
      sortable: true,
    },
    { title: t("action"), class: "action", key: "action", sortable: false },
  ];
  initDefaultData();
  loading.value = false;
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.resource-listing {
  flex: 0 0 100%;
  max-width: 100%;
  border-radius: 10px;

  > .wrapper {
    display: flex;
    align-items: center;
    flex: 0 0 100%;
    flex-flow: row wrap;
    justify-content: flex-end;
    padding: $spacing-05;
    background-color: $ui-01;
    border-radius: 10px 10px 0 0;

    .overflow-menu-button {
      margin-left: auto;
    }
  }

  .data-table-list {
    > .v-table {
      > .v-table__wrapper {
        border-radius: 0 0 10px 10px;
      }
    }
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    word-break: break-word;

    @include media("mobile") {
      flex: 0 0 100%;

      ~ .bx--btn {
        margin: $spacing-05 auto 0;
      }
    }

    @include media("tablet-min") {
      margin-right: $spacing-05;
    }

    .bx--link {
      @include carbon--type-style("body-short-02");
      position: relative;
      display: flex;
      align-items: center;
      min-width: 40px;
      color: $interactive-02;
      cursor: pointer;
      text-decoration: none;

      &:hover {
        color: $hover-secondary;
        text-decoration: underline;
      }

      // For "My Drive" to break legibility
      &:first-of-type {
        min-width: 4.25rem;
        &:only-of-type {
          pointer-events: none;
        }
      }

      &:last-child {
        color: $text-01;
      }

      &:not(:only-of-type):last-of-type {
        pointer-events: none;
      }

      span {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }

    .bx--overflow-menu {
      height: 22px;
      &:hover {
        background-color: inherit;
      }
      .bx--overflow-menu__trigger {
        margin-top: $spacing-02;
        transform: rotate(90deg);
        &:hover {
          background-color: $hover-ui;
        }
      }
    }

    .bx--link,
    .bx--overflow-menu {
      &:not(:first-child) {
        margin-left: $spacing-05;
        padding-left: $spacing-05;
        &:before {
          content: "";
          position: absolute;
          left: -0.5rem;
          width: 1rem;
          height: 1rem;

          background-image: url("/src/assets/images/icons/indicative/16px/ForwardSlash16.svg");
        }
      }
    }
  }
}
</style>
